const STORAGE_KEY_PREFIX_DAILY = "dailyLevelStats";
const DEFAULT_STORAGE_SCOPE = "default";
const DAILY_SCHEMA_VERSION = 3;

function canUseLocalStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function normalizeStorageScope(value) {
  const safe = typeof value === "string" ? value.trim() : "";
  return safe || DEFAULT_STORAGE_SCOPE;
}

function dailyStorageKey(scope = DEFAULT_STORAGE_SCOPE) {
  return `${STORAGE_KEY_PREFIX_DAILY}:${normalizeStorageScope(scope)}`;
}

function loadDaily(scope = DEFAULT_STORAGE_SCOPE) {
  if (!canUseLocalStorage()) return {};
  try {
    const raw = window.localStorage.getItem(dailyStorageKey(scope));
    const parsed = JSON.parse(raw || "{}");
    return isPlainObject(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function saveDaily(daily, scope = DEFAULT_STORAGE_SCOPE) {
  if (!canUseLocalStorage()) return;
  try {
    window.localStorage.setItem(
      dailyStorageKey(scope),
      JSON.stringify(daily || {})
    );
  } catch (e) {
    console.warn("[levelSessions] Failed saving local daily stats:", e);
  }
}

function nowTs() {
  return Date.now();
}

function dayKeyFrom(ts) {
  const safeTs = Math.max(0, Number(ts) || 0);
  const d = new Date(safeTs);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function fmtDuration(ms) {
  const abs = Math.max(0, Number(ms) || 0);
  const h = Math.floor(abs / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const s = Math.floor((abs % 60000) / 1000);
  const msLeft = abs % 1000;

  const parts = [];
  if (h) parts.push(`${h}h`);
  if (m || h) parts.push(`${m}m`);
  if (s || m || h) parts.push(`${s}s`);
  parts.push(`${msLeft}ms`);
  return parts.join(" ");
}

function emptyDay() {
  return {
    totals: { totalMs: 0, sessions: 0, completed: 0 },
    levels: Object.create(null),
  };
}

function sanitizeTitle(title, fallback) {
  return typeof title === "string" && title.trim() ? title.trim() : fallback;
}

function toSafeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function normalizeLevelId(levelId) {
  const safe = typeof levelId === "string" ? levelId.trim() : "";
  return safe || "";
}

function logDayToConsole(dayKey, day) {
  console.log(`[Daily Summary] ${dayKey}`);

  const totals = day?.totals || { totalMs: 0, sessions: 0, completed: 0 };
  console.log(
    `- Totals: ${fmtDuration(totals.totalMs)} across ${totals.sessions} session(s) [completed: ${totals.completed}]`
  );

  const levels = isPlainObject(day?.levels) ? day.levels : {};
  const ids = Object.keys(levels);

  if (ids.length === 0) {
    console.log("- No levels recorded.");
    return;
  }

  ids.forEach((id) => {
    const entry = levels[id] || {};
    console.log(
      `  • ${entry.title || id}: ${fmtDuration(entry.totalMs)} across ${entry.sessions || 0} [completed: ${entry.completed || 0}]`
    );
  });
}

function getStorageScopeFromFirestoreConfig(firestore) {
  return normalizeStorageScope(
    firestore?.kidId || firestore?.uid || DEFAULT_STORAGE_SCOPE
  );
}

const state = () => ({
  currentSession: null,
  daily: loadDaily(DEFAULT_STORAGE_SCOPE),
  storageScope: DEFAULT_STORAGE_SCOPE,
  firestore: {
    enabled: false,
    db: null,
    uid: "",
    kidId: "",
    auth: null,
    writeRawSessions: false,
  },
});

const getters = {
  inProgress: (s) => !!(s.currentSession && !s.currentSession.endTime),
  currentSession: (s) => s.currentSession,
  daily: (s) => s.daily,
  todayKey: () => dayKeyFrom(nowTs()),
  todayStats: (s, g) => s.daily[g.todayKey] || emptyDay(),
  dayStats: (s) => (key) => s.daily[key] || emptyDay(),
};

const mutations = {
  setCurrentSession(state, session) {
    state.currentSession = session;
  },

  endCurrentSession(state, { endTime, finished }) {
    if (!state.currentSession || state.currentSession.endTime) return;
    state.currentSession.endTime = Math.max(0, toSafeNumber(endTime));
    state.currentSession.finished = !!finished;
  },

  setDaily(state, daily) {
    state.daily = isPlainObject(daily) ? daily : {};
  },

  setStorageScope(state, scope) {
    state.storageScope = normalizeStorageScope(scope);
  },

  clearCurrentSession(state, expectedSessionId = null) {
    if (!expectedSessionId) {
      state.currentSession = null;
      return;
    }

    if (state.currentSession && state.currentSession.id === expectedSessionId) {
      state.currentSession = null;
    }
  },

  setFirestoreConfig(state, cfg) {
    state.firestore = {
      enabled: false,
      db: null,
      uid: "",
      kidId: "",
      auth: null,
      writeRawSessions: false,
      ...(cfg || {}),
    };
  },
};

const actions = {
  async configureBackend({ commit }, payload = {}) {
    const firestore =
      isPlainObject(payload) && isPlainObject(payload.firestore)
        ? payload.firestore
        : { enabled: false };

    commit("setFirestoreConfig", firestore);

    const nextStorageScope = getStorageScopeFromFirestoreConfig(firestore);
    commit("setStorageScope", nextStorageScope);
    commit("setDaily", loadDaily(nextStorageScope));
  },

  startSession({ state, commit, dispatch }, { levelId, title } = {}) {
    const safeLevelId = normalizeLevelId(levelId);
    if (!safeLevelId) {
      console.warn("[levelSessions] startSession called without a valid levelId");
      return;
    }

    if (state.currentSession && !state.currentSession.endTime) {
      dispatch("abortSession").catch((e) => {
        console.warn("[levelSessions] Failed aborting previous session:", e);
      });
    }

    const startedAt = nowTs();

    const session = {
      id: `${safeLevelId}-${startedAt}-${Math.random().toString(36).slice(2, 8)}`,
      levelId: safeLevelId,
      title: sanitizeTitle(title, safeLevelId),
      startTime: startedAt,
      endTime: null,
      finished: false,
    };

    commit("setCurrentSession", session);
  },

  finishSession({ state, commit, dispatch }) {
    if (!state.currentSession || state.currentSession.endTime) {
      return Promise.resolve();
    }

    commit("endCurrentSession", { endTime: nowTs(), finished: true });
    return dispatch("persistCurrentSession");
  },

  abortSession({ state, commit, dispatch }) {
    if (!state.currentSession || state.currentSession.endTime) {
      return Promise.resolve();
    }

    commit("endCurrentSession", { endTime: nowTs(), finished: false });
    return dispatch("persistCurrentSession");
  },

  finalizeIfNeeded({ state, dispatch }) {
    if (state.currentSession && !state.currentSession.endTime) {
      return dispatch("abortSession");
    }
    return Promise.resolve();
  },

  persistCurrentSession({ state, commit, dispatch }) {
    const liveSession = state.currentSession;
    if (!liveSession || !liveSession.endTime) return Promise.resolve();

    const safeStartTime = Math.max(0, toSafeNumber(liveSession.startTime));
    const safeEndTime = Math.max(safeStartTime, toSafeNumber(liveSession.endTime));
    const safeLevelId = normalizeLevelId(liveSession.levelId);

    if (!safeLevelId) {
      console.warn("[levelSessions] Cannot persist session without a valid levelId");
      commit("clearCurrentSession", liveSession.id);
      return Promise.resolve();
    }

    const session = {
      id: liveSession.id,
      levelId: safeLevelId,
      title: sanitizeTitle(liveSession.title, safeLevelId),
      startTime: safeStartTime,
      endTime: safeEndTime,
      finished: !!liveSession.finished,
    };

    const durationMs = Math.max(0, session.endTime - session.startTime);
    const dayKey = dayKeyFrom(session.endTime);
    const safeTitle = sanitizeTitle(session.title, session.levelId);

    const daily = { ...(isPlainObject(state.daily) ? state.daily : {}) };
    const existingDay = isPlainObject(daily[dayKey]) ? daily[dayKey] : emptyDay();

    const day = {
      totals: {
        totalMs: toSafeNumber(existingDay.totals?.totalMs),
        sessions: toSafeNumber(existingDay.totals?.sessions),
        completed: toSafeNumber(existingDay.totals?.completed),
      },
      levels: Object.assign(
        Object.create(null),
        isPlainObject(existingDay.levels) ? existingDay.levels : {}
      ),
    };

    day.totals.totalMs += durationMs;
    day.totals.sessions += 1;
    if (session.finished) {
      day.totals.completed += 1;
    }

    const existingLevel = isPlainObject(day.levels[session.levelId])
      ? day.levels[session.levelId]
      : {
          title: safeTitle,
          totalMs: 0,
          sessions: 0,
          completed: 0,
        };

    day.levels[session.levelId] = {
      title: sanitizeTitle(existingLevel.title, safeTitle),
      totalMs: toSafeNumber(existingLevel.totalMs) + durationMs,
      sessions: toSafeNumber(existingLevel.sessions) + 1,
      completed: toSafeNumber(existingLevel.completed) + (session.finished ? 1 : 0),
    };

    daily[dayKey] = day;

    commit("setDaily", daily);
    saveDaily(daily, state.storageScope);

    console.log(
      `[Level Exit] "${safeTitle}" | Start: ${new Date(session.startTime).toLocaleString()} | End: ${new Date(session.endTime).toLocaleString()} | Duration: ${fmtDuration(durationMs)} | Finished: ${session.finished} | Day: ${dayKey}`
    );
    logDayToConsole(dayKey, day);

    commit("clearCurrentSession", session.id);

    if (
      state.firestore.enabled &&
      state.firestore.db &&
      state.firestore.uid &&
      state.firestore.kidId
    ) {
      return dispatch("syncSessionToFirestore", {
        session,
        durationMs,
        dayKey,
      }).catch((e) => {
        console.warn("[levelSessions] Firestore sync failed:", e);
      });
    }

    return Promise.resolve();
  },

  async syncSessionToFirestore({ state }, { session, durationMs, dayKey }) {
    const { db, uid, kidId, auth, writeRawSessions } = state.firestore || {};

    if (!db || !uid || !kidId || !session?.id || !session?.levelId || !dayKey) {
      throw new Error("Missing Firestore config or session data");
    }

    const safeDurationMs = Math.max(0, toSafeNumber(durationMs));
    const safeStartTime = Math.max(0, toSafeNumber(session.startTime));
    const safeEndTime = Math.max(safeStartTime, toSafeNumber(session.endTime));
    const safeCompleted = session.finished ? 1 : 0;
    const safeTitle = sanitizeTitle(session.title, session.levelId);
    const dailyId = `${kidId}_${dayKey}`;

    const { getAuth } = await import("firebase/auth");
    const activeAuth = auth || getAuth();
    const authUid = activeAuth?.currentUser?.uid || "";

    if (!authUid) {
      throw new Error("No signed-in Firebase user. Firestore write skipped.");
    }

    if (authUid !== uid) {
      throw new Error(
        `Configured Firestore uid "${uid}" does not match authenticated uid "${authUid}".`
      );
    }

    const { doc, setDoc, serverTimestamp, increment } = await import("firebase/firestore");

    const dayRef = doc(db, "dailyStats", dailyId);

    const levelPatch = Object.create(null);
    levelPatch[session.levelId] = {
      title: safeTitle,
      totalMs: increment(safeDurationMs),
      sessions: increment(1),
      completed: increment(safeCompleted),
    };

    await setDoc(
      dayRef,
      {
        schemaVersion: DAILY_SCHEMA_VERSION,
        userId: uid,
        kidId,
        date: dayKey,
        totalMs: increment(safeDurationMs),
        sessions: increment(1),
        completed: increment(safeCompleted),
        updatedAt: serverTimestamp(),
        levels: levelPatch,
      },
      { merge: true }
    );

    if (writeRawSessions) {
      const rawRef = doc(db, "users", uid, "sessions", session.id);
      await setDoc(
        rawRef,
        {
          id: session.id,
          userId: uid,
          kidId,
          levelId: session.levelId,
          title: safeTitle,
          date: dayKey,
          startTime: safeStartTime,
          endTime: safeEndTime,
          durationMs: safeDurationMs,
          finished: !!session.finished,
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};